/** @jsxImportSource @emotion/react */
import { Alert, Button, Icon, Intent, Tooltip } from "@blueprintjs/core"
import { useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { isEmpty } from "lodash"
import { Dispatch, KeyboardEvent, SetStateAction, useCallback, useEffect, useState } from "react"
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form"

import { FlexContainer, FlexContainerAndItem, FlexItem } from "../../../../styled/flexbox.styled"
import { SCIENT_COLORS } from "../../../../styled/scientColors"
import { LargeText, MediumText } from "../../../../styled/text.styled"
import { ButtonWithAlert } from "../../../components"
import { createToaster } from "../../../utils/createToaster"
import LiveOrdersSwitch from "../Portfolio/components/LiveOrdersSwitch"
import useBooksQuery from "../Portfolio/hooks/useBooksQuery"
import useTradingAccountsQuery from "../Portfolio/hooks/useTradingAccountsQuery"
import { assertOrderEquals, isWaveEmpty } from "../utils/checkEmptyWave"
import { convertWaveInputsToWave } from "../utils/formatSendWave"
import CSVUploader from "./CSVUploader"
import ManualShortLocateModal from "./ManualShortLocateModal"
import OrdersTable from "./OrdersTable"
import { addRowButtonCss, FormContainer, FormFooterContainer } from "./WaveCreationForm.styled"
import AccountBookSelect from "./components/AccountBookSelect"
import useCreateWaveMutation from "./hooks/useCreateWaveMutation"
import { getDefaultOrderInputs, OrderInputs, WaveInputs } from "./types/form"
import { Order } from "./types/order"
import { Wave } from "./types/wave"
import { getWaveResultToaster } from "./utils/waveResultToaster"
import { ShortLocateForm } from "./components/CreateShortLocateForm"

export interface IWaveCreateForm {
  setSelectedWaveId: Dispatch<SetStateAction<string | number | undefined>>
  directOrdersEnabled: boolean
  defaultOrder: OrderInputs
  locateInstrumentQuery?: string
  locateQuantity?: number
  setLocateInstrumentQuery: Dispatch<SetStateAction<string | undefined>>
  setLocateQuantity: Dispatch<SetStateAction<number | undefined>>
  shortLocateModalOpen: boolean
  setShortLocateModalOpen: Dispatch<SetStateAction<boolean>>
  setLiveOrders: Dispatch<SetStateAction<boolean>>
  liveOrders: boolean
  previousWave?: Wave
}

const WaveCreationForm = ({
  setSelectedWaveId,
  directOrdersEnabled,
  defaultOrder,
  locateInstrumentQuery,
  locateQuantity,
  setLocateInstrumentQuery,
  setLocateQuantity,
  shortLocateModalOpen,
  setShortLocateModalOpen,
  setLiveOrders,
  liveOrders,
  previousWave,
}: IWaveCreateForm) => {
  const queryClient = useQueryClient()

  // Initialize form in WaveCreationForm component
  const methods = useForm<WaveInputs>({
    defaultValues: {
      rational: "",
      orders: Array(2).fill(defaultOrder),
    },
    mode: "onChange",
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    setValue,
    getValues,
    reset: resetWaveForm,
  } = methods

  // Monitor or retrieve the values of the form
  const watchedOrders = useWatch({ name: "orders", control }) ?? getValues("orders")
  const watchedAccountId = useWatch({ name: "account_id", control }) ?? getValues("account_id")
  const watchedBookId = useWatch({ name: "book_id", control }) ?? getValues("book_id")

  // Initialize orders array
  const {
    fields,
    append: appendOrder,
    remove: removeOrder,
    update: updateOrder,
  } = useFieldArray({
    control,
    name: "orders",
  })

  /**
   * Getting all accounts that user has access to
   */
  const { accounts, error: accountsError } = useTradingAccountsQuery({ enabled: true })
  const { books } = useBooksQuery(
    { accountIds: watchedAccountId != null ? [watchedAccountId] : [] },
    { enabled: Boolean(watchedAccountId) },
  )

  useEffect(() => {
    if (accountsError) {
      createToaster({
        message: "An error occurred while fetching your account, please contact us using intercom.",
        intent: Intent.DANGER,
      })
    }
  }, [accountsError])

  /**
   * Consistent reset function that properly clears all form fields
   */
  const performFormReset = useCallback(() => {
    resetWaveForm(
      {
        rational: "",
        orders: Array(2).fill(defaultOrder),
      },
      { keepValues: false, keepDefaultValues: false },
    )
  }, [resetWaveForm, defaultOrder])

  /**
   * Copy values from the previous order at the given index
   */
  const copyPreviousOrder = useCallback(
    (index: number) => {
      const watchOrders = watch("orders")
      const { id, ...previousOrder } = watchOrders[index - 1]
      if (previousOrder && !assertOrderEquals(previousOrder, defaultOrder)) {
        updateOrder(index, previousOrder)
        trigger(`orders[${index}]`)
      }
    },
    [updateOrder, watch, trigger, defaultOrder],
  )

  /**
   * Create/Update/NewRevision Wave Mutations
   */
  const createWaveMutation = useCreateWaveMutation({
    onSuccess: (wave: Wave) => {
      const toasterConfig = getWaveResultToaster(wave)
      createToaster(toasterConfig)

      queryClient.removeQueries(["waves"])
      performFormReset()
      setSelectedWaveId(wave.id)
    },
    onError: (error: AxiosError) => {
      createToaster({
        message: "Error while creating wave",
        intent: Intent.DANGER,
      })
    },
  })

  // Duplicate confirmation state
  const [isDuplicateConfirmOpen, setIsDuplicateConfirmOpen] = useState(false)
  const [pendingWave, setPendingWave] = useState<Partial<Wave> | null>(null)

  /**
   * Callback to submit Form
   */
  const submitForm = useCallback(
    (data: WaveInputs) => {
      const newWave = convertWaveInputsToWave(data, defaultOrder)
      // Duplicate check versus previous wave (order-level minimal match)
      const prevOrders: Order[] = previousWave?.orders || []
      const newOrders: Order[] = (newWave.orders as Order[]) || []

      const round2 = (n?: number) => (typeof n === "number" ? Number(n.toFixed(2)) : undefined)

      const ordersMatchMinimal = (a: Order, b: Order) => {
        const aInstId = a.instrument?.id
        const bInstId = b.instrument?.id
        if (aInstId !== bInstId) return false
        if (a.book_id !== b.book_id) return false
        if (a.side !== b.side) return false
        if (a.order_type !== b.order_type) return false

        // If limit order, limit_price must match (rounded 2dp)
        if (a.order_type === "limit") {
          if (round2(a.limit_price) !== round2(b.limit_price)) return false
        }

        // Quantity/Amount minimal rule
        const aHasQty = typeof a.quantity === "number"
        const bHasQty = typeof b.quantity === "number"
        const aHasAmt = typeof a.amount_usd === "number"
        const bHasAmt = typeof b.amount_usd === "number"

        if (aHasAmt && bHasAmt) return round2(a.amount_usd) === round2(b.amount_usd)
        if (aHasQty && bHasQty) return a.quantity === b.quantity
        return false
      }

      const hasDuplicate = newOrders.some(n => prevOrders.some(p => ordersMatchMinimal(n, p)))

      if (hasDuplicate) {
        setPendingWave(newWave)
        setIsDuplicateConfirmOpen(true)
        return
      }

      createWaveMutation.mutate(newWave)
    },
    [createWaveMutation, defaultOrder, watchedAccountId, watchedBookId, previousWave],
  )

  // Function to not allow wave submission when user presses 'enter' key
  const handleKeyPress = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
    }
  }

  const handleShortLocateModalClose = () => {
    setShortLocateModalOpen(false)
    setLocateInstrumentQuery(undefined)
    setLocateQuantity(undefined)
  }

  return (
    <FormProvider {...methods}>
      <FormContainer onSubmit={handleSubmit(submitForm)} onKeyDown={handleKeyPress}>
        <FlexContainer flexDirection="column">
          <OrdersTable
            orders={fields}
            accounts={accounts}
            control={control}
            register={register}
            deleteOrder={removeOrder}
            copyPreviousOrder={copyPreviousOrder}
            errors={errors}
            watch={watch}
            directOrdersEnabled={directOrdersEnabled}
            setValue={setValue}
            defaultOrder={getDefaultOrderInputs(directOrdersEnabled)}
          />
          <Button
            text={
              <FlexContainer>
                <LargeText fontWeight="bold">+</LargeText>
                {(watchedOrders?.length || 0) > 99 && (
                  <MediumText>&nbsp; Max orders of 100 reached</MediumText>
                )}
              </FlexContainer>
            }
            onClick={() => appendOrder(defaultOrder)}
            disabled={(watchedOrders?.length || 0) > 99}
            css={addRowButtonCss}
          />
        </FlexContainer>
        <FormFooterContainer flexDirection="row-reverse" gap="10px" alignItems="center">
          <FlexItem>
            <Button
              large
              type="submit"
              text="Submit"
              disabled={!isEmpty(errors) || isWaveEmpty(watchedOrders || [], defaultOrder)}
              intent={Intent.PRIMARY}
              icon="send-message"
              loading={createWaveMutation.isLoading}
            />
          </FlexItem>
          <FlexItem>
            <ButtonWithAlert
              buttonProps={{
                text: "Reset",
                fill: true,
                icon: "refresh",
                large: true,
              }}
              alertProps={{
                intent: Intent.DANGER,
                confirmButtonText: "Yes, reset",
                onConfirm: performFormReset,
              }}
            >
              <p>Do you really want to reset the table?</p>
            </ButtonWithAlert>
          </FlexItem>
          <FlexContainerAndItem gap="10px" alignItems="center">
            <CSVUploader resetWaveForm={performFormReset} setValue={setValue} />
            <Tooltip
              content={
                <MediumText color={SCIENT_COLORS.darkGray5}>
                  CSV columns names are case-insensitive and their names are:
                  <br />
                  <br />
                  <i>
                    Ticker, Side, USD, Qty, Type, Limit, Broker, Execution, Auction, CAP, Time in
                    market, Target end, Comment
                  </i>
                  <br />
                  <br />
                  It is allowed to have only certain columns in the CSV.
                </MediumText>
              }
              placement="top"
              compact
              hoverOpenDelay={500}
            >
              <Icon className="sct-button-in-button" icon="info-sign" size={16} />
            </Tooltip>
          </FlexContainerAndItem>
          <FlexItem>
            {directOrdersEnabled && (
              <Button
                large
                text="Locates"
                icon="locate"
                onClick={() => setShortLocateModalOpen(true)}
              />
            )}
          </FlexItem>
          <LiveOrdersSwitch setLiveOrders={setLiveOrders} isLiveOrders={liveOrders} />
          <FlexContainerAndItem flexGrow={1} gap="10px">
            <AccountBookSelect control={control} name="account_id" options={accounts} />
            <AccountBookSelect control={control} name="book_id" options={books} />
          </FlexContainerAndItem>
        </FormFooterContainer>
      </FormContainer>
      <ManualShortLocateModal
        isOpen={shortLocateModalOpen}
        onClose={handleShortLocateModalClose}
        locateInstrumentQuery={locateInstrumentQuery}
        locateQuantity={locateQuantity}
      />
      <Alert
        className="bp5-dark"
        cancelButtonText="Cancel"
        confirmButtonText="Send anyway"
        icon="duplicate"
        intent={Intent.WARNING}
        isOpen={isDuplicateConfirmOpen}
        onCancel={() => {
          setIsDuplicateConfirmOpen(false)
          setPendingWave(null)
        }}
        onConfirm={() => {
          if (pendingWave) {
            createWaveMutation.mutate(pendingWave)
          }
          setIsDuplicateConfirmOpen(false)
          setPendingWave(null)
        }}
      >
        <p>
          You are about to submit orders identical to your previous wave. Do you want to continue?
        </p>
      </Alert>
    </FormProvider>
  )
}

export default WaveCreationForm
